import { useLoaderData } from "react-router-dom";

import { get } from "@/api";
import { Models } from "@/api/types";
import { DiscountMap, GiftCardsShopContainer } from "@/modules/giftCards";
import { queryClient } from "@/pages/Root";
import { loadIfFlag } from "@/services/Flags";
import { waitObj } from "@/toolbox/Promise";

async function loader() {
  try {
    const platform = await queryClient.fetchQuery(["/platform"], () =>
      get("/platform")
    );

    const giftCardData = await queryClient.fetchQuery(["/gift-cards"], () =>
      get("/gift-cards", {})
    );

    const user = await queryClient.fetchQuery({
      queryKey: ["/user"],
      queryFn: () => get("/user"),
      staleTime: 1000 * 60 * 2,
    });

    const discountPromises: Promise<DiscountMap>[] = giftCardData.map((d) =>
      queryClient
        .fetchQuery(["/shop/discounts/listings/{listingId}", d.id], () =>
          get("/shop/discounts/listings/{listingId}", {
            listingId: d.id,
            includeInclusionsAndRequirementsPackageNames: true,
          })
        )
        .then((res) => res.packageDiscounts ?? {})
    );

    const results: DiscountMap[] = await Promise.all(discountPromises);

    const availablePromotions: DiscountMap = Object.assign({}, ...results);

    return waitObj({
      giftCards: giftCardData,
      platform,
      user,
      availablePromotions,
    });
  } catch (error) {
    console.error("Failed to load gift cards:", error);
    throw new Response("Failed to load gift cards", { status: 500 });
  }
}

export const shopGiftCardsLoader = loadIfFlag("giftcards")(() => {
  return loader();
});

const ShopGiftCardsPage = () => {
  const {
    giftCards: giftCardsFromLoader,
    user,
    platform,
    availablePromotions,
  } = useLoaderData() as {
    giftCards: Models<"DisplayGiftCardListing">[];
    platform: Models<"PlatformModel">;
    user: Models<"UserModel1">;
    availablePromotions: DiscountMap;
  };

  return (
    <div className="ShopGiftCardsPage">
      <GiftCardsShopContainer
        giftCards={giftCardsFromLoader}
        platform={platform}
        currentUser={user}
        availablePromotions={availablePromotions}
      />
    </div>
  );
};

export default ShopGiftCardsPage;
